import { getMessage } from '@/shared/copy/messages';
import { ApiError, type JsonValue } from '@/shared/data/client';
import { getErrorMessage } from '@/shared/data/errors';

type ClerkInviteErrorEntry = {
  code?: string;
  long_message?: string;
  longMessage?: string;
  message?: string;
};

type ClerkInviteErrorPayload = {
  errors?: ClerkInviteErrorEntry[];
};

const parseClerkInvitePayload = (message: string): ClerkInviteErrorPayload | null => {
  const jsonStart = message.indexOf('{');
  if (jsonStart < 0) {
    return null;
  }

  const rawPayload = message.slice(jsonStart).trim();
  try {
    return JSON.parse(rawPayload) as ClerkInviteErrorPayload;
  } catch {
    return null;
  }
};

const resolveClerkInviteMessage = (payload: ClerkInviteErrorPayload): string | null => {
  const firstError = payload.errors?.[0];
  if (!firstError) {
    return null;
  }

  switch (firstError.code) {
    case 'already_a_member_in_organization':
      return getMessage('organisation.invites.alreadyMember');
    case 'unsupported_content_type':
      return getMessage('organisation.invites.cancelFailed');
    case undefined:
      return firstError.long_message ?? firstError.longMessage ?? firstError.message ?? null;
    default:
      return firstError.long_message ?? firstError.longMessage ?? firstError.message ?? null;
  }
};

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

export const normalizeInviteError = (err: unknown): ApiError => {
  const apiError = toApiError(err);
  const payload = parseClerkInvitePayload(apiError.message);
  const resolvedMessage = payload ? resolveClerkInviteMessage(payload) : null;

  if (!resolvedMessage) {
    return apiError;
  }

  return new ApiError(resolvedMessage, {
    status: apiError.status,
    code: apiError.code,
    details: apiError.details as JsonValue | undefined,
    errorId: apiError.errorId,
    correlationId: apiError.correlationId,
  });
};
